import { buildOrderProgressModel } from "@/utils/orderProgress.js";
import "@/components/order-progress.css";

function StepIcon({ stepId, state, fulfillmentType }) {
    const stroke = state === "upcoming" || state === "cancelled" ? "currentColor" : "#fff";
    const common = {
        viewBox: "0 0 24 24",
        width: "1em",
        height: "1em",
        fill: "none",
        stroke,
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true
    };

    if (state === "done") {
        return (
            <svg {...common}>
                <path d="M20 6 9 17l-5-5" />
            </svg>
        );
    }

    if (stepId === "received" || stepId === "pending") {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
            </svg>
        );
    }

    if (stepId === "confirmed") {
        return (
            <svg {...common}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="m9 15 2 2 4-4" />
            </svg>
        );
    }

    if (stepId === "preparing") {
        return (
            <svg {...common}>
                <path d="M6 7h15l-1.5 9h-12z" />
                <path d="M6 7 5 3H2" />
                <circle cx="9" cy="20" r="1" />
                <circle cx="18" cy="20" r="1" />
            </svg>
        );
    }

    if (stepId === "ready") {
        if (fulfillmentType === "DELIVERY") {
            return (
                <svg {...common}>
                    <path d="M3 7h11v10H3z" />
                    <path d="M14 10h4l3 3v4h-7z" />
                    <circle cx="7" cy="19" r="1.5" />
                    <circle cx="17" cy="19" r="1.5" />
                </svg>
            );
        }
        return (
            <svg {...common}>
                <path d="M4 10 12 4l8 6v10H4z" />
                <path d="M9 20v-6h6v6" />
            </svg>
        );
    }

    return (
        <svg {...common}>
            <path d="M20 6 9 17l-5-5" />
        </svg>
    );
}

/**
 * Visual pipeline for a customer or admin order.
 * @param {"compact"|"detail"|"admin"} variant
 */
export default function OrderProgressTracker({ order, variant = "detail", className = "" }) {
    const mode = variant === "admin" ? "admin" : "customer";
    const model = buildOrderProgressModel(order, { mode });
    const showPageTitle = variant === "detail" || variant === "admin";
    const showLabels = variant === "detail" || variant === "admin";
    const rootClass = [
        "order-progress",
        `order-progress--${variant}`,
        model.cancelled ? "order-progress--cancelled" : "",
        className
    ].filter(Boolean).join(" ");

    return (
        <div className={rootClass}>
            <div className="order-progress-heading">
                {showPageTitle ? (
                    <h1 className="order-progress-headline">{model.headline}</h1>
                ) : (
                    <strong className="order-progress-headline">{model.headline}</strong>
                )}
                {model.meta && <p className="muted order-progress-meta">{model.meta}</p>}
            </div>

            <div className="order-progress-pipeline">
                <ol className="order-progress-track" aria-label="Suivi de la commande">
                    {model.steps.map((step, index) => {
                        const connectorFilled = !model.cancelled && model.currentIndex >= index;
                        return (
                            <li
                                key={step.id}
                                className={`order-progress-step order-progress-step--${step.state}`}
                                aria-current={step.state === "current" ? "step" : undefined}
                            >
                                {index > 0 && (
                                    <span
                                        className={`order-progress-connector${connectorFilled ? " order-progress-connector--filled" : ""}`}
                                        aria-hidden="true"
                                    />
                                )}
                                <span className="order-progress-node" title={step.label}>
                                    <StepIcon
                                        stepId={step.id}
                                        state={step.state}
                                        fulfillmentType={model.fulfillmentType}
                                    />
                                </span>
                            </li>
                        );
                    })}
                </ol>
                {showLabels && (
                    <div className="order-progress-labels" aria-hidden="true">
                        {model.steps.map((step) => (
                            <span
                                key={step.id}
                                className={`order-progress-step-label order-progress-step-label--${step.state}`}
                            >
                                {step.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {model.caption && (
                <p className="muted order-progress-caption">{model.caption}</p>
            )}
        </div>
    );
}
